import classNames from "classnames";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { LuCalendar, LuChevronDown } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import CustomCurrencyInput from "../../../components/CustomCurrencyInput";
import { StyledDatePicker } from "../../../components/datepicker/datepicker";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { useAppSelector } from "../../../redux/redux";
import {
  IAddPayment,
  IInvoiceView,
  Invoice,
  useAddPaymentMutation,
  useGetPaymentMethodsQuery,
} from "../../../services/BillingService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { serverMessage } from "../../../utils/serverMessage";
import { transformDateToUTC } from "../../../utils/transformDate";
import {
  BILLING_DATE_PICKER_FORMAT,
  formatBillingDate,
  isPaymentDateAllowed,
  PAYMENT_DATE_ERROR,
  paymentDateBounds,
} from "../billingDates.utils";
import {
  BILLING_PRIMARY_BUTTON,
  BILLING_SECONDARY_BUTTON,
  formatMoney,
} from "../ui/BillingUi";
import {
  lineAmount,
  overpaymentWarning,
  paymentAmountProblem,
} from "./paymentAmounts.utils";

interface Props {
  close: () => void;
  invoice: Invoice | IInvoiceView | undefined;
  refetch?: () => void;
}

const PayInvoiceModal: React.FC<Props> = ({ close, invoice, refetch }) => {
  const navigate = useNavigate();

  const { user } = useAppSelector((state) => state.appReducer);

  const [addPayment, { isSuccess: isSuccessAdd, error }] =
    useAddPaymentMutation();

  const { data: paymentMethods, isLoading: isLoadingMethods } =
    useGetPaymentMethodsQuery(getLinkedFirm()?.orgId);

  const [amountPaid, setAmountPaid] = useState("");

  // Payment date: between the invoice date and today (Roadmap 1809).
  const dateBounds = useMemo(
    () => paymentDateBounds(invoice?.invoiceDate),
    [invoice?.invoiceDate],
  );
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [method, setMethod] = useState("");
  const [notes, setNotes] = useState("");
  const [showDateError, setShowDateError] = useState(false);
  // PAY-013 / PAY-014 (QA batch 17): refused amount / overpayment question.
  const [amountProblem, setAmountProblem] = useState<string | null>(null);
  const [overpayQuestion, setOverpayQuestion] = useState<string | null>(null);
  const isDateAllowed = isPaymentDateAllowed(paymentDate, dateBounds);

  useEffect(() => {
    // An invoice dated in the future: start on its own date, not today.
    if (dateBounds.minDate && moment().isBefore(dateBounds.minDate, "day")) {
      setPaymentDate(dateBounds.minDate);
    }
  }, [dateBounds.minDate]);

  useEffect(() => {
    if (paymentMethods && paymentMethods?.length > 0) {
      setMethod(paymentMethods[0].id);
    }
  }, [paymentMethods]);

  useEffect(() => {
    if (isSuccessAdd) {
      close();
      navigate("/billing/invoices");
    }
  }, [isSuccessAdd]);

  const createPayment = () => {
    if (!isDateAllowed) {
      setShowDateError(true);
      return;
    }

    const lines = [
      {
        amountPaid,
        balance: invoice?.balance || 0,
        invoiceNo:
          (invoice as Invoice)?.invoiceNumber || invoice?.invoiceNo || null,
      },
    ];
    const problem = paymentAmountProblem(lines);
    setAmountProblem(problem);
    if (problem) return;
    const question = overpaymentWarning(lines);
    if (question && !overpayQuestion) {
      setOverpayQuestion(question);
      return;
    }

    if (invoice?.customerId && paymentDate) {
      const body: IAddPayment = {
        paymentDate: paymentDate.toISOString(),
        customerId: invoice?.customerId,
        amount: lineAmount(amountPaid),
        allowOverpayment: !!question,
        balance: invoice?.balance,
        notes: notes,
        orgId: getLinkedFirm()?.orgId,
        paymentMethodId: method,
        userId: user?.id,
        paymentItem: [
          {
            invoiceId: invoice?.id,
            amountPaid: lineAmount(amountPaid),
          },
        ],
      };
      addPayment(body);
    }
  };

  const invoiceNumber =
    (invoice as Invoice)?.invoiceNumber || invoice?.invoiceNo || "";
  const amountError =
    amountProblem ||
    (!!error && serverMessage(error, "Something went wrong"));

  return (
    <div
      id={"pay-invoice-modal"}
      style={{ marginTop: 0, marginBottom: 0 }}
      className={
        "fixed left-0 top-0 z-[60] flex h-screen w-screen items-center justify-center bg-[rgba(20,22,34,0.42)] p-6 font-Manrope"
      }
      onClick={close}>
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={"Record a payment"}
        className={
          "w-[460px] max-w-full overflow-hidden rounded-2xl bg-white shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
        }
        onClick={(event) => event.stopPropagation()}>
        <div className={"border-b border-[#EFF1F4] px-[26px] py-5"}>
          <h3 className={"m-0 text-lg font-extrabold text-[#1F2433]"}>
            Record a payment
          </h3>
          <div
            className={
              "mt-1.5 flex items-center gap-4 text-[13px] text-[#8B92A2]"
            }>
            <span>
              Invoice{" "}
              <span className={"font-bold text-[#3A4256]"}>
                #{invoiceNumber}
              </span>
            </span>
            <span className={"h-[3px] w-[3px] rounded-full bg-[#C6CBD6]"} />
            <span>
              Due{" "}
              <span className={"font-bold text-[#3A4256]"}>
                {formatBillingDate(invoice?.dueDate)}
              </span>
            </span>
          </div>
        </div>

        {isLoadingMethods ? (
          <LottieLoading />
        ) : (
          <div className={"px-[26px] pb-6 pt-[22px]"}>
            <div
              className={
                "mb-5 flex items-center justify-between rounded-[10px] border border-[#EEF0F3] bg-[#F7F8FA] px-4 py-3"
              }>
              <span className={"text-[13px] font-semibold text-[#6B7185]"}>
                Balance due
              </span>
              <span
                className={
                  "text-lg font-extrabold tabular-nums text-[#1F2433]"
                }>
                {formatMoney(invoice?.balance)}
              </span>
            </div>

            <div className={"mb-4 grid grid-cols-2 gap-3.5"}>
              <div className={"flex min-w-0 flex-col gap-[7px]"}>
                <span className={"text-[12.5px] font-bold text-[#3A4256]"}>
                  Payment date
                </span>
                <div
                  className={
                    "flex h-[42px] items-center justify-between gap-2 overflow-hidden rounded-[9px] border border-[#DFE2E8] bg-white pl-3 pr-2.5"
                  }>
                  <div className={"min-w-0 flex-1"}>
                    <StyledDatePicker
                      value={paymentDate}
                      minDate={dateBounds.minDate}
                      maxDate={dateBounds.maxDate}
                      format={BILLING_DATE_PICKER_FORMAT}
                      acceptTypedDates
                      styleType={"ghost"}
                      fontSize={"13.5px"}
                      width={"120px"}
                      onChange={(date) => {
                        setShowDateError(false);
                        setPaymentDate(transformDateToUTC(date));
                      }}
                    />
                  </div>
                  <LuCalendar size={15} className={"shrink-0 text-[#9AA0B0]"} />
                </div>
              </div>
              <label className={"flex min-w-0 flex-col gap-[7px]"}>
                <span className={"text-[12.5px] font-bold text-[#3A4256]"}>
                  Method
                </span>
                <div className={"relative"}>
                  <select
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                    className={
                      "h-[42px] w-full cursor-pointer appearance-none rounded-[9px] border border-[#DFE2E8] bg-white pl-3 pr-8 text-[13.5px] text-[#1F2433] outline-none focus:border-[#6B4EFF]"
                    }>
                    {paymentMethods?.map((item) => (
                      <option value={item?.id} key={item?.id}>
                        {item?.paymentMethod}
                      </option>
                    ))}
                  </select>
                  <LuChevronDown
                    size={14}
                    className={
                      "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA0B0]"
                    }
                  />
                </div>
              </label>
            </div>

            <div className={"mb-4 flex flex-col gap-[7px]"}>
              <span className={"text-[12.5px] font-bold text-[#3A4256]"}>
                Amount paid
              </span>
              <CustomCurrencyInput
                allowNegative={false}
                value={amountPaid}
                setValue={(value) => {
                  setAmountProblem(null);
                  setOverpayQuestion(null);
                  setAmountPaid(value);
                }}
                className={
                  "!h-[42px] !rounded-[9px] !border-[#DFE2E8] !px-3.5 !text-sm tabular-nums focus:!border-[#6B4EFF] focus:!outline-none"
                }
              />
            </div>

            <label className={"flex flex-col gap-[7px]"}>
              <span className={"text-[12.5px] font-bold text-[#3A4256]"}>
                Notes
              </span>
              <textarea
                value={notes}
                placeholder={"Add a note (optional)..."}
                onChange={(e) => setNotes(e.target.value)}
                className={
                  "min-h-[72px] resize-y rounded-[9px] border border-[#DFE2E8] px-3.5 py-2.5 text-[13px] text-[#1F2433] outline-none placeholder:text-[#9AA0B0] focus:border-[#6B4EFF]"
                }
              />
            </label>

            {showDateError && !isDateAllowed && (
              <div
                role={"alert"}
                className={
                  "mt-4 rounded-lg border border-[#F5D0D0] bg-[#FDF3F3] px-3 py-2 text-[13px] text-[#B42318]"
                }>
                {PAYMENT_DATE_ERROR}
              </div>
            )}

            {overpayQuestion && (
              <div
                role={"alert"}
                className={
                  "mt-4 rounded-lg border border-[#F5E3B5] bg-[#FFFBEB] px-3 py-2 text-[13px] text-[#8A5A00]"
                }>
                {overpayQuestion}
              </div>
            )}

            {amountError && (
              <div
                role={"alert"}
                className={
                  "mt-4 rounded-lg border border-[#F5D0D0] bg-[#FDF3F3] px-3 py-2 text-[13px] text-[#B42318]"
                }>
                {amountError}
              </div>
            )}
          </div>
        )}

        <div
          className={
            "flex items-center justify-end gap-3.5 border-t border-[#EFF1F4] bg-[#FCFCFD] px-[26px] py-4"
          }>
          <button
            type={"button"}
            onClick={close}
            className={BILLING_SECONDARY_BUTTON}>
            Cancel
          </button>
          <button
            type={"button"}
            onClick={createPayment}
            className={classNames(BILLING_PRIMARY_BUTTON, "px-[26px]")}>
            {overpayQuestion ? "Record anyway" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayInvoiceModal;
